import React, {useState, useCallback, useMemo} from "react";
import {buildStyles} from "../../../../connectui/component/buildStyles";
import {useTheme} from "../../../../connectui/theme/ThemeProvider";
import ListItemContainer from "../../../../connectui/component/layout/list/ListItemContainer";
import Container from "../../../../connectui/component/layout/Container";
import Text from "../../../../connectui/component/display/Text";
import ChipText from "../../../../connectui/component/display/chip/ChipText";
import FlexContainer from "connectui/component/layout/FlexContainer";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import Tooltip from "connectui/component/display/Tooltip";
import {formatCount} from "utilities/helperFunctions";

const useStyles = (theme) =>
  buildStyles({
    itemRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(3),
      width: "100%",
    },
    hoverActionsContainer: {
      width: "auto",
      overflow: "visible",
      flex: 1,
      height: "20px",
      marginRight: theme.spacing(-2),
    },
    hoverIcon: {
      width: "16px",
      height: "16px",
    },
    editIcon: {
      width: "16px",
      height: "16px",
      color: theme.colors.textSecondary,
      display: "inline-flex !important",
      marginLeft: theme.spacing(2),
      verticalAlign: "text-bottom",
    },
  });

const FiltersSetItem = ({
  icon = null,
  id,
  label,
  description,
  onEditName,
  customLabel,
  dropdownContent = null,
  totalCount = null,
  openContextMenu,
  selected = false,
  ...props
}) => {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);
  const [isHovered, setIsHovered] = useState(false);
  const {itemProps = {}, ...rest} = props;

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const tooltipContent = useMemo(() => {
    return description ? (
      <Text
        variant="textXS"
        color="white"
        sx={{maxWidth: 250, textWrap: "auto"}}
      >
        {description}
      </Text>
    ) : null;
  }, [description]);

  const renderItem = () => (
    <ListItemContainer
      defaultCollapsed
      nestedList={dropdownContent}
      indentSpacing={theme.spacing(8)}
      itemProps={{
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        ...itemProps,
      }}
      {...rest}
    >
      <Container className={classes.itemRow}>
        {icon}
        <Text
          as="div"
          variant="textSM"
          weight="medium"
          color="gray700"
          sx={{display: "inline"}}
        >
          {label}
          {!!onEditName && (isHovered || selected || isMobile) && (
            <SvgIcon
              className={classes.editIcon}
              component={ICONS.edit}
              onClick={onEditName}
            />
          )}
        </Text>
        <FlexContainer
          className={classes.hoverActionsContainer}
          alignItems="center"
          justifyContent="flex-end"
          gap={theme.spacing(2)}
        >
          {totalCount !== null && (!isHovered || id === "0") && (
            <Tooltip
              tooltipContent={
                Number(totalCount) > 99 ? (
                  <Text variant="textXS" weight="semibold" color="white">
                    {totalCount}
                  </Text>
                ) : null
              }
            >
              <ChipText
                text={formatCount(Number(totalCount))}
                sx={{
                  minWidth: "fit-content",
                  "&:hover": {backgroundColor: theme.colors.primary100},
                }}
              />
            </Tooltip>
          )}
          {id !== "0" && (isHovered || isMobile) && (
            <FlexContainer alignItems="center" gap={theme.spacing(2)}>
              <ButtonIcon
                sx={{
                  padding: theme.spacing(1),
                  border: "none",
                  boxShadow: "none",
                  backgroundColor: "transparent",
                  borderRadius: "50%",
                  color: theme.colors.gray700,
                  "&:hover": {
                    backgroundColor: theme.colors.gray200,
                  },
                }}
                icon={
                  <SvgIcon
                    className={classes.hoverIcon}
                    component={ICONS["more-vertical"]}
                  />
                }
                onClick={openContextMenu}
              />
            </FlexContainer>
          )}
        </FlexContainer>
      </Container>
    </ListItemContainer>
  );

  return isMobile ? (
    renderItem()
  ) : (
    <Tooltip
      tooltipContent={tooltipContent}
      enterDelay={500}
      leaveDelay={0}
      colors={{bg: "gray900", text: "white"}}
      sx={{tooltip: {padding: theme.spacing(2)}}}
    >
      {renderItem()}
    </Tooltip>
  );
};

export default FiltersSetItem;
